import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Cake,
  CalendarClock,
  CalendarCheck2,
  ChevronRight,
  CircleCheck,
  Ear,
  Bell,
  Banknote,
  PackageX,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  birthday: { icon: Cake, label: "Cumpleaños", color: "bg-[#F3E5D8] text-[#2A2F2C]" },
  review: { icon: CalendarClock, label: "Revisión anual", color: "bg-[#D1E0D7] text-[#2A2F2C]" },
  followup_loss: { icon: Ear, label: "Seguimiento (pérdida)", color: "bg-[#E8D1D1] text-[#2A2F2C]" },
  followup_noloss: { icon: Ear, label: "Seguimiento anual", color: "bg-[#EAE6DF] text-[#2A2F2C]" },
  stock_low: { icon: PackageX, label: "Stock bajo", color: "bg-[#E8D1D1] text-[#2A2F2C]" },
  appointment_today: { icon: CalendarCheck2, label: "Cita hoy", color: "bg-[#D1E0D7] text-[#2A2F2C]" },
  financing_due: { icon: Banknote, label: "Cobro pendiente", color: "bg-[#F3E5D8] text-[#7C5A2A]" },
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const ack = async (notif_id) => {
    try {
      await api.post("/notifications/ack", { notif_id });
      setItems((prev) => prev.filter((n) => n.id !== notif_id));
      toast.success("Notificación marcada");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
            Avisos internos
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
            Notificaciones
          </h1>
          <p className="text-sm text-[#6E7772] mt-1">
            Cumpleaños, revisiones anuales y seguimientos automáticos.
          </p>
        </div>
        <Bell className="h-6 w-6 text-[#4A5D4E]" strokeWidth={1.5} />
      </div>

      {loading ? (
        <div className="text-[#6E7772]">Cargando…</div>
      ) : items.length === 0 ? (
        <Card className="bg-white border-[#E5E3DB] shadow-none">
          <CardContent className="p-12 text-center text-[#6E7772]">
            <CircleCheck strokeWidth={1.5} className="h-10 w-10 mx-auto mb-3 text-[#4A5D4E]" />
            Sin avisos pendientes. ¡Todo al día!
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((n) => {
            const meta = TYPE_META[n.type] || { icon: Bell, label: n.type, color: "bg-[#EAE6DF]" };
            const Icon = meta.icon;
            return (
              <Card
                key={n.id}
                data-testid={`notif-${n.id}`}
                className="bg-white border-[#E5E3DB] shadow-none hover:bg-[#F7F6F3] transition-colors"
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div
                    className={`h-10 w-10 rounded-full flex items-center justify-center ${meta.color}`}
                  >
                    <Icon strokeWidth={1.5} className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[#2A2F2C] truncate">{n.title}</span>
                      <Badge className={meta.color}>{meta.label}</Badge>
                      {n.priority === "high" && (
                        <Badge className="bg-[#E8D1D1] text-[#2A2F2C]">Urgente</Badge>
                      )}
                    </div>
                    <div className="text-sm text-[#6E7772] mt-0.5">{n.message}</div>
                  </div>
                  {(n.patient_id || n.stock_id || n.appointment_id) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (n.type === "stock_low") {
                          navigate("/stock");
                        } else if (n.type === "appointment_today") {
                          navigate("/citas");
                        } else if (n.patient_id) {
                          navigate(`/pacientes/${n.patient_id}`);
                        }
                      }}
                      data-testid={`notif-open-${n.id}`}
                    >
                      Abrir <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => ack(n.id)}
                    data-testid={`notif-ack-${n.id}`}
                  >
                    Listo
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
